import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import "../../components/home/home.css";
import "./Service.css";
import ThreeBackground from "../../components/common/ThreeBackground";
import Icon from "../../components/common/Icon";
import { serviceCategories, solutionPillars, enterpriseSystems } from "../../Data/siteContent";
import collabizLogo from "../../assets/logos/collabiz.svg";
import { useSeo, SEO } from "../../config/seo";

const ease = [0.21, 0.8, 0.25, 1];
const PILLAR_ACCENT = { cyan: "#34e6ff", electric: "#4f8cff", violet: "#8b5cf6", coffee: "#c98a52" };

export default function Service() {
  useSeo(SEO.solutions);
  const location = useLocation();
  const [active, setActive] = useState(serviceCategories[0].id);
  const railRef = useRef(null);

  // Scroll to hash on load
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 140);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  // Scroll-spy to highlight the active pill
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    serviceCategories.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  // Keep the active pill visible in the rail
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const pill = rail.querySelector(`[data-pill="${active}"]`);
    if (pill) pill.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [active]);

  return (
    <main className="cv-home cv-services">
      {/* A. Hero */}
      <header className="cv-svc-hero">
        <ThreeBackground variant="orbs" />
        <div className="cv-svc-hero__inner">
          <motion.span className="cv-eyebrow" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <span className="dot" /> Solutions
          </motion.span>
          <motion.h1 className="cv-svc-hero__title" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08, ease }}>
Solutions that <span className="cv-gradient-text">run inside the business</span>
          </motion.h1>
          <motion.p className="cv-svc-hero__sub" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.16, ease }}>
Four solution pillars, eleven delivery domains, and 150+ projects shipped. We connect what you already run, and build what you don't.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.24, ease }}>
            <Link to="/contactus" className="cv-btn">Book Free Consultation <Icon name="arrow" size={18} className="arrow" /></Link>
          </motion.div>
        </div>
      </header>

      {/* A2. Solution pillars */}
      <section className="cv-section cv-svc-pillars">
        <div className="cv-heading cv-heading--left">
          <span className="cv-eyebrow"><span className="dot" /> Solution pillars</span>
          <h2 className="cv-h2">Start with the shape of the problem.</h2>
          <p className="cv-lead">
            Most engagements land in one of four places. Each one starts with the systems and data
            you already have, not a blank page.
          </p>
        </div>

        <div className="cv-sol__grid">
          {solutionPillars.map((p, i) => (
            <motion.article
              key={p.id}
              id={p.id}
              className="cv-sol__card"
              style={{ "--accent": PILLAR_ACCENT[p.accent] || PILLAR_ACCENT.cyan }}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: (i % 2) * 0.08, ease }}
            >
              <div className="cv-sol__head">
                <span className="cv-sol__ic"><Icon name={p.icon} size={22} /></span>
                <div>
                  <h3 className="cv-sol__name">{p.name}</h3>
                  <span className="cv-sol__kicker">{p.kicker}</span>
                </div>
              </div>
              <p className="cv-sol__desc">{p.description}</p>
              <ul className="cv-sol__outcomes">
                {p.outcomes.map((o) => (
                  <li key={o}><Icon name="check" size={15} />{o}</li>
                ))}
              </ul>
              <Link to={`/contactus?project=${encodeURIComponent(p.name)}`} className="cv-svc-card__quote">
                Talk about this <Icon name="arrow" size={15} className="arrow" />
              </Link>
            </motion.article>
          ))}
        </div>

        <motion.div
          className="cv-sol__systems"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, ease }}
        >
          <div className="cv-sol__systems-head">
            <h3>Systems we connect to</h3>
            <p>
              Bring your own ERP and we integrate with it. Outgrown spreadsheets but not ready for
              enterprise licensing? We deploy CollaBiz, our own B2B/ERP platform, instead.
            </p>
          </div>
          <div className="cv-sol__marks">
            {enterpriseSystems.map((sys) => (
              <div key={sys.id} className={"cv-sol__mark" + (sys.own ? " is-own" : "")}>
                <span className="cv-sol__mark-text">
                  {sys.own && <img src={collabizLogo} alt="" className="cv-sol__mark-logo" />}
                  {sys.mark}
                </span>
                <span className="cv-sol__mark-note">{sys.note}</span>
                {sys.own && <span className="cv-sol__mark-badge">Ours</span>}
              </div>
            ))}
          </div>
          <p className="cv-sol__disclaimer">
            Product names are trademarks of their respective owners. CafeVista builds integrations
            and extensions for these systems; we are not an authorised reseller or implementation
            partner of any of them.
          </p>
        </motion.div>
      </section>

      {/* B. Sticky pills */}
      <nav className="cv-svc-pills" aria-label="Service categories">
        <div className="cv-svc-pills__rail" ref={railRef}>
          {serviceCategories.map((c) => (
            <a
              key={c.id}
              data-pill={c.id}
              href={`#${c.id}`}
              className={`cv-svc-pill cv-acc-${c.accent} ${active === c.id ? "is-active" : ""}`}
            >
              <Icon name={c.icon} size={15} />
              {c.name}
            </a>
          ))}
        </div>
      </nav>

      {/* C. Category sections */}
      {serviceCategories.map((c) => (
        <section key={c.id} id={c.id} className={`cv-svc-cat cv-acc-${c.accent}`}>
          <div className="cv-svc-cat__inner">
            <motion.div className="cv-svc-cat__head" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, ease }}>
              <span className="cv-svc-cat__ic"><Icon name={c.icon} size={30} /></span>
              <div>
                <h2 className="cv-svc-cat__title">{c.name}</h2>
                <p className="cv-svc-cat__value">{c.value}</p>
              </div>
            </motion.div>

            <div className="cv-svc-cat__grid">
              {c.items.map((item, i) => (
                <motion.div
                  key={item}
                  className="cv-svc-card cv-glass"
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: i * 0.07, ease }}
                >
                  <span className="cv-svc-card__ic"><Icon name={c.icon} size={20} /></span>
                  <h3>{item}</h3>
                  <Link to={`/contactus?project=${encodeURIComponent(item)}`} className="cv-svc-card__quote">
                    Get a Quote <Icon name="arrow" size={15} className="arrow" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* D. Bottom CTA */}
      <section className="cv-section cv-section--tight">
        <motion.div className="cv-svc-cta cv-glass" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, ease }}>
          <div className="cv-prod-cta__glow" aria-hidden="true" />
          <h2>Can't find what you need? <span className="cv-gradient-text">We build custom from scratch.</span></h2>
          <p>If it can be designed, coded, automated, or manufactured — bring it to us.</p>
          <div className="cv-svc-cta__actions">
            <Link to="/contactus" className="cv-btn">Talk to an Expert <Icon name="arrow" size={18} className="arrow" /></Link>
            <Link to="/home#work" className="cv-btn cv-btn--ghost">View Our Work</Link>
          </div>
        </motion.div>
      </section>
    </main>
  );
}
